import { useState, useEffect, useCallback } from "react";
import { useQuery } from "@tanstack/react-query";
import InvoiceTable, { Invoice } from "./Components/InvoiceTable/InvoiceTable";
import InvoiceHeader from "./Components/InvoiceTable/InvoiceHeader";
import StatusFilter from "./Components/StatusFilter/StatusFilter";
import UninvoicedPanel from "./Components/UninvoicedPanel/UninvoicedPanel";
import "./InvoicePage.css";
import { useSearchParams } from "react-router-dom";
import InvoiceTopbar from "./Components/TopBar/InvoiceTopbar";
import InvoiceService, { UninvoicedInvoiceScope } from "../../AppSettings/services/InvoiceService";
import { isEngineeringEnvironment, isFeatureEnabled } from "../../AppSettings/environmentConfig";
import { useRuntimeConfig } from "../../__settings__/runtimeConfig";

type StatusValue = "Un-Paid" | "Partial" | "Paid" | "Void" | null;
type ViewMode = "table" | "card";

const InvoicePage: React.FC = () => {
  useRuntimeConfig();
  const standardEngineering = isEngineeringEnvironment && !isFeatureEnabled('bids');
  const [showCreateInvoice, setShowCreateInvoice] = useState(false);
  const [filteredInvoices, setFilteredInvoices] = useState<Invoice[]>([]);
  const [statusFilter, setStatusFilter] = useState<StatusValue>(null);
  const [viewMode, setViewMode] = useState<ViewMode>("card");
  const [searchParams] = useSearchParams();
  const [showUninvoicedPanel, setShowUninvoicedPanel] = useState(false);
  const [prefillClientId, setPrefillClientId] = useState<string | undefined>();
  const [prefillMatterId, setPrefillMatterId] = useState<string | undefined>();
  const [prefillScope, setPrefillScope] = useState<UninvoicedInvoiceScope | undefined>();

  const columns: Record<string, keyof Invoice | string> = {
    "Invoices": "invoiceid",
    "Created At": "created_at",
    "Due Date": "due_date",
    "Total Amount": "total_amount",
    "Amount Paid": "amount_paid",
  };

  const { data: invoices = [], isPending: loading, refetch: refetchInvoices } = useQuery({
    queryKey: ['invoices-all'],
    queryFn: async () => {
      const data = await InvoiceService.getAllInvoices();
      return data;
    },
    staleTime: 2 * 60 * 1000,
  });

  const { data: uninvoicedCount = 0, refetch: refetchUninvoiced } = useQuery({
    queryKey: ['uninvoiced-count', standardEngineering],
    queryFn: async () => {
      const data = await InvoiceService.getUninvoicedSummary(standardEngineering ? { non_pipeline: true } : undefined);
      return data.reduce(
        (sum, c) => sum + c.matters.reduce((ms, m) => ms + m.uninvoiced_count, 0),
        0
      );
    },
    staleTime: 2 * 60 * 1000,
  });

  const fetchInvoices = useCallback(() => { refetchInvoices(); }, [refetchInvoices]);
  const fetchUninvoicedCount = useCallback(() => { refetchUninvoiced(); }, [refetchUninvoiced]);

  useEffect(() => {
    if (searchParams.get("create") === "true") {
      setShowCreateInvoice(true);
    }
  }, [searchParams]);

  const deriveInvoiceStatus = (invoice: Invoice): StatusValue => {
    if (invoice.status === "void") return "Void";
    const amountPaid = parseFloat((invoice.amount_paid as string) || "0");
    const totalAmount = parseFloat((invoice.total_amount as string) || "0");
    // "Paid" = nothing still owed. amountPaid >= totalAmount also covers a $0
    // invoice (0 >= 0), which the old `totalAmount > 0` guard left stuck (M4).
    if (amountPaid >= totalAmount) return "Paid";
    if (amountPaid > 0) return "Partial";
    return "Un-Paid";
  };

  const applyStatusFilter = (list: Invoice[]): Invoice[] => {
    if (statusFilter) {
      return list.filter((invoice) => deriveInvoiceStatus(invoice) === statusFilter);
    }
    // "All" hides voided invoices; user must click "Void" to see them
    return list.filter((invoice) => invoice.status !== "void");
  };

  useEffect(() => {
    if (!invoices) return;
    setFilteredInvoices(applyStatusFilter([...invoices]));
  }, [invoices, statusFilter]);

  const handleSearch = (search: string, filter?: string | null) => {
    if (!filter || !search) {
      if (invoices) {
        setFilteredInvoices(applyStatusFilter([...invoices]));
      }
      return;
    }

    let filtered: Invoice[];
    if (filter === "Invoices") {
      filtered = invoices.filter((invoice) => {
        const invoiceId = (invoice.invoiceid || "").toString().toLowerCase();
        const searchLower = search.toLowerCase();
        return invoiceId.includes(searchLower);
      });
    } else {
      filtered = invoices.filter((invoice) => {
        const columnKey = columns[filter];
        const fieldValue = (invoice as Record<string, unknown>)[columnKey as string];
        if (fieldValue == null) return false;
        return fieldValue.toString().toLowerCase().includes(search.toLowerCase());
      });
    }

    setFilteredInvoices(applyStatusFilter(filtered));
  };

  const handleStatusFilterChange = (status: StatusValue | string | null) => {
    setStatusFilter(status as StatusValue);
  };

  const handleInvoiceCreated = async () => {
    setShowCreateInvoice(false);
    setPrefillClientId(undefined);
    setPrefillMatterId(undefined);
    setPrefillScope(undefined);
    setStatusFilter(null);
    fetchInvoices();
    fetchUninvoicedCount();
  };

  const handleCreateFromUninvoiced = (clientId: string, matterId: string, scope?: UninvoicedInvoiceScope) => {
    setShowUninvoicedPanel(false);
    setPrefillClientId(clientId);
    setPrefillMatterId(matterId);
    setPrefillScope(scope);
    setShowCreateInvoice(true);
  };

  const handleCreateFullInvoiceForClient = (clientId: string, scope?: UninvoicedInvoiceScope) => {
    setShowUninvoicedPanel(false);
    setPrefillClientId(clientId);
    setPrefillMatterId(undefined);
    setPrefillScope(scope);
    setShowCreateInvoice(true);
  };

  return (
    <div>
      <InvoiceTopbar onSearch={handleSearch} />
      <InvoiceHeader
        onCreateInvoice={() => {
          setPrefillClientId(undefined);
          setPrefillMatterId(undefined);
          setPrefillScope(undefined);
          setShowCreateInvoice(true);
        }}
        viewMode={viewMode}
        onViewModeChange={setViewMode}
        onOpenUninvoiced={() => setShowUninvoicedPanel((isOpen) => !isOpen)}
        uninvoicedCount={uninvoicedCount}
      />
      <StatusFilter
        activeStatus={statusFilter}
        onStatusChange={handleStatusFilterChange}
        allData={invoices}
      />
      <InvoiceTable
        showCreateInvoice={showCreateInvoice}
        invoices={filteredInvoices}
        onInvoiceCreated={handleInvoiceCreated}
        loading={loading}
        viewMode={viewMode}
        initialClientId={prefillClientId}
        initialMatterId={prefillMatterId}
        initialScope={prefillScope}
      />
      {showUninvoicedPanel && (
        <UninvoicedPanel
          onClose={() => setShowUninvoicedPanel(false)}
          onCreateInvoice={handleCreateFromUninvoiced}
          onCreateInvoiceForClient={handleCreateFullInvoiceForClient}
        />
      )}
    </div>
  );
};

export default InvoicePage;
